import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Alert, Button, FormRow } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaBan, FaCaretDown, FaCaretRight, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import PropTypes from 'prop-types';
import { CRM_LEGAL_STATUS, CRM_NUMBER_OF_EMPLOYEES, CRM_TYPE_OF_CUSTOMER } from 'app_config';
import { salesEmployeesByNameDebounce } from 'helpers/employeeOptionHelper';
import { createCustomer } from 'redux/crm/customerSlice';
import { getCountries } from 'redux/settingsSlice';
import { getIndustries } from 'redux/crm/settingSlice';
import GoogleMapAutoCompletePlaceDebounce from 'components/common/google-map-places/GoogleMapAutoCompletePlaceDebounce';
import CustomerUploadLogo from 'components/app/crm/customer/CustomerUploadLogo';
import { INPUT_CLS, SELECT_STYLES } from 'helpers/formStyles';

const Section = ({ title, open, onToggle, children }) => (
  <div className="tw-border tw-border-slate-200 tw-rounded tw-overflow-hidden tw-mb-3">
    <button
      type="button"
      onClick={onToggle}
      className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-bg-slate-50 hover:tw-bg-slate-100 tw-transition-colors tw-border-0 tw-cursor-pointer tw-text-left"
    >
      {open ? <FaCaretDown size={13} className="tw-text-slate-500" /> : <FaCaretRight size={13} className="tw-text-slate-500" />}
      <span className="tw-text-sm tw-font-semibold tw-text-slate-700">{title}</span>
    </button>
    {open && (
      <div className="tw-px-4 tw-py-4 tw-flex tw-flex-col tw-gap-1">
        {children}
      </div>
    )}
  </div>
);

Section.propTypes = {
  title:    PropTypes.string.isRequired,
  open:     PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const CustomerFormModal = ({ showModal, setshowModal }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();
  const { industryData: industries } = useSelector(state => state.crm_setting);
  const { countryData } = useSelector(state => state.settings);
  const { data: profileUser } = useSelector(state => state.profile_user);
  const [address, setAddress] = useState({});
  const [countryCode, setCountryCode] = useState('vn');
  const [logo, setLogo] = useState('');
  const [open, setOpen] = useState({ info: true, legal: true, responsible: true, logo: false });
  const dispatch = useDispatch();

  useEffect(() => {
    setValue('country', profileUser?.site?.country);
    setValue('main_contact', profileUser.name);
    setValue('type_of_company', CRM_TYPE_OF_CUSTOMER[0]);
    setValue('industry', industries[0]?.name);
    if (industries.length === 0) dispatch(getIndustries());
    if (countryData.length === 0) dispatch(getCountries());
    // mount-only: seed defaults + load lookups once; re-running when industries load would reset user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  const closeModal = () => setshowModal(false);

  const numberOfEmployeesOptions = useMemo(() => CRM_NUMBER_OF_EMPLOYEES.map(opt => ({ value: opt, label: opt })), []);
  const typeOfCustomerOptions    = useMemo(() => CRM_TYPE_OF_CUSTOMER.map(opt => ({ value: opt, label: opt })), []);
  const industryOptions          = useMemo(() => (industries || []).map(item => ({ value: item.name, label: item.name })), [industries]);
  const countryOptions           = useMemo(() => (countryData || []).map(opt => ({ value: opt.country, label: opt.name })), [countryData]);

  const defaultCountryOption     = useMemo(() => countryOptions.find(c => c.value === 'VN'), [countryOptions]);
  const defaultTypeOption        = useMemo(() => typeOfCustomerOptions[0], [typeOfCustomerOptions]);
  const defaultEmployeesOption   = useMemo(() => numberOfEmployeesOptions[0], [numberOfEmployeesOptions]);
  const defaultIndustryOption    = useMemo(() => industryOptions[0], [industryOptions]);
  const defaultMainContactOption = useMemo(() => ({ value: profileUser.name, label: profileUser.name }), [profileUser.name]);

  const validatePhoneNumber = (number) => {
    if (!number || number === 'undefined' || number.trim() === '') return undefined;
    const countryVal = getValues().country;
    const code = countryVal?.toUpperCase();
    if (!isValidPhoneNumber(number.toString(), code)) {
      return `Invalid phone number with country code '${code}'`;
    }
    return true;
  };

  const validate = (data) => {
    if ((!data.email || data.email === '') && (!data.telephone || data.telephone === '')) {
      toast.error('Email or mobile must be required.');
      return false;
    }
    return true;
  };

  const onSubmit = async (data) => {
    if (!validate(data)) return;

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach(item => formData.append(`${key}[]`, item));
      } else {
        formData.append(key, value);
      }
    });
    if (logo) formData.append('logo', logo[0]);
    if (address) formData.append('address', JSON.stringify(address));

    try {
      const response = await dispatch(createCustomer(formData));
      if (response.payload?.data?.id) closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadSalesEmpOptions = (inputValue, callback) => {
    salesEmployeesByNameDebounce(inputValue, callback);
  };

  const footer = (
    <>
      <Button type="submit" size="sm" onClick={handleSubmit(onSubmit)}>
        <FaSave size={12} /> Create Customer
      </Button>
      <Button variant="danger" size="sm" onClick={closeModal}>
        <FaBan size={12} /> Cancel
      </Button>
    </>
  );

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title="New Customer"
      width="50%"
      backdropClick={false}
      footer={footer}
    >
      <Alert type="warning" className="tw-mb-4" message="Create a new customer." />

      {/* 1. Information */}
      <Section title="1. Information" open={open.info} onToggle={() => toggle('info')}>
        <FormRow label="Company name" required error={errors.name?.message}>
          <input
            type="text"
            placeholder="Company name"
            className={INPUT_CLS}
            {...register('name', { required: 'Company name must be required.' })}
          />
        </FormRow>

        <FormRow label="Country" required error={errors.country?.message}>
          <Controller
            control={control}
            name="country"
            rules={{ required: 'Country is required' }}
            render={() => (
              <Select
                closeMenuOnSelect
                options={countryOptions}
                defaultValue={defaultCountryOption}
                placeholder="Select..."
                menuPosition="fixed"
                styles={SELECT_STYLES}
                onChange={opt => {
                  setValue('country', opt.value);
                  setCountryCode(opt.value.toLowerCase());
                }}
              />
            )}
          />
        </FormRow>

        <FormRow label="Work Email" error={errors.email?.message}>
          <input
            type="email"
            placeholder="example@pebsteel.com.vn"
            className={INPUT_CLS}
            {...register('email', {
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: 'Invalid email address',
              },
            })}
          />
        </FormRow>

        <FormRow label="Work phone" error={errors.telephone?.message}>
          <Controller
            control={control}
            name="telephone"
            rules={{ validate: validatePhoneNumber }}
            render={({ field: { ref, ...field } }) => (
              <PhoneInput
                {...field}
                inputProps={{ ref }}
                country={countryCode}
                autoFormat
                countryCodeEditable={false}
                enableSearch
                disableSearchIcon
                inputStyle={{ width: '100%' }}
                searchStyle={{ width: '95%' }}
              />
            )}
          />
        </FormRow>

        <FormRow label="Fax" error={errors.fax?.message}>
          <Controller
            control={control}
            name="fax"
            rules={{ validate: validatePhoneNumber }}
            render={({ field: { ref, ...field } }) => (
              <PhoneInput
                {...field}
                inputProps={{ ref }}
                country={countryCode}
                autoFormat
                countryCodeEditable={false}
                enableSearch
                disableSearchIcon
                inputStyle={{ width: '100%' }}
                searchStyle={{ width: '95%' }}
              />
            )}
          />
        </FormRow>

        <FormRow label="EXT internal">
          <input
            type="text"
            placeholder="123"
            className={INPUT_CLS}
            {...register('ext_phone')}
          />
        </FormRow>

        <FormRow label="Tax code">
          <input
            type="text"
            placeholder="12345678"
            className={INPUT_CLS}
            {...register('tax_code')}
          />
        </FormRow>

        <FormRow label="Website">
          <input
            type="text"
            placeholder="https://example.com"
            className={INPUT_CLS}
            {...register('website')}
          />
        </FormRow>

        <FormRow label="Address">
          <GoogleMapAutoCompletePlaceDebounce setAddress={setAddress} />
        </FormRow>
      </Section>

      {/* 2. Legal status */}
      <Section title="2. Legal status" open={open.legal} onToggle={() => toggle('legal')}>
        <FormRow label="Legal status">
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            {CRM_LEGAL_STATUS.map(status => (
              <label key={status} className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer">
                <input
                  type="radio"
                  name="legal_status"
                  value={status}
                  defaultChecked={status === 'Private'}
                  className="tw-accent-blue-600"
                  {...register('legal_status')}
                />
                {status}
              </label>
            ))}
          </div>
        </FormRow>

        <FormRow label="Type of customer" required error={errors.type_of_company?.message}>
          <Controller
            control={control}
            name="type_of_company"
            rules={{ required: 'Type of customer is required' }}
            render={() => (
              <Select
                closeMenuOnSelect
                options={typeOfCustomerOptions}
                defaultValue={defaultTypeOption}
                placeholder="Select..."
                menuPosition="fixed"
                styles={SELECT_STYLES}
                onChange={opt => setValue('type_of_company', opt.value)}
              />
            )}
          />
        </FormRow>

        <FormRow label="Number of employees">
          <Controller
            control={control}
            name="employess"
            render={() => (
              <Select
                closeMenuOnSelect
                options={numberOfEmployeesOptions}
                defaultValue={defaultEmployeesOption}
                placeholder="Select..."
                menuPosition="fixed"
                styles={SELECT_STYLES}
                onChange={opt => setValue('employess', opt.value)}
              />
            )}
          />
        </FormRow>

        <FormRow label="Industry" required error={errors.industry?.message}>
          <Controller
            control={control}
            name="industry"
            rules={{ required: 'Industry is required' }}
            render={() => (
              <Select
                closeMenuOnSelect
                options={industryOptions}
                defaultValue={defaultIndustryOption}
                placeholder="Select..."
                menuPosition="fixed"
                styles={SELECT_STYLES}
                onChange={opt => setValue('industry', opt.value)}
              />
            )}
          />
        </FormRow>
      </Section>

      {/* 3. Responsible */}
      <Section title="3. Responsible" open={open.responsible} onToggle={() => toggle('responsible')}>
        <FormRow label="Main contact" required error={errors.main_contact?.message}>
          <Controller
            control={control}
            name="main_contact"
            rules={{ required: 'Main contact is required' }}
            render={() => (
              <AsyncSelect
                cacheOptions
                defaultOptions
                defaultValue={defaultMainContactOption}
                loadOptions={loadSalesEmpOptions}
                closeMenuOnSelect
                placeholder="Select..."
                menuPosition="fixed"
                styles={SELECT_STYLES}
                onChange={opt => setValue('main_contact', opt.value)}
              />
            )}
          />
        </FormRow>

        <FormRow label="Secondary contact">
          <Controller
            control={control}
            name="secondary_contact"
            render={() => (
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadSalesEmpOptions}
                closeMenuOnSelect={false}
                placeholder="Select..."
                menuPosition="fixed"
                styles={SELECT_STYLES}
                isMulti
                onChange={opts => setValue('secondary_contact', opts.map(o => o.value), { shouldValidate: true })}
              />
            )}
          />
        </FormRow>
      </Section>

      {/* 4. Upload logo */}
      <Section title="4. Upload logo" open={open.logo} onToggle={() => toggle('logo')}>
        <CustomerUploadLogo logo={logo} setLogo={setLogo} customer={null} />
      </Section>
    </Drawer>
  );
};

CustomerFormModal.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
};

export default CustomerFormModal;
